import React from 'react'
// import { ReactComponent as CaloriesIcon } from '../../../../assets/icons/flame.svg'
import { ReactComponent as CaloriesIcon } from '../../../../assets/v2/icons/icon-activity-calories-white.svg'
import { ReactComponent as HeartIcon } from '../../../../assets/icons/heart.svg'
// import { ReactComponent as StepsIcon } from '../../../../assets/icons/steps.svg'
import { ReactComponent as StepsIcon } from '../../../../assets/v2/icons/icon-activity-steps-white.svg'
import { formatThousands } from '../../../../common/helpers/translations'
import { CustomType, ProgressType } from '../../../../services/api.types'
import { Tooltip } from '../../../Tooltip/Tooltip'
import './ProgressCell.scss'
import { ReactComponent as MinutesIcon } from '../../../../assets/v2/icons/icon-activity-minutes.svg'
import { msgid, ngettext } from 'ttag'
import { isDistanceType, isPersistenceType } from '../../../../common/helpers/challenges'
import { customUnits, getCustomUnitText } from '../../../../services/CustomTypes'

export interface IProgressCellProps {
  /**
   * Number of calories burned in total.
   */
  caloriesTotal?: number
  /**
   * Number of intensive minutes in total.
   */
  intensiveMinutesTotal?: number
  /**
   * Number of steps in total.
   */
  stepsTotal?: number
  /**
   * Type of the current goal.
   */
  progressType?: ProgressType
  /**
   * Progress completion percent
   */
  progress?: number

  /**
   * Progress description
   */
  description?: string

  /**
   * Activity formula target
   */
  activityFormulaTarget?: number

  /**
   * Persistence total
   */
  persistenceTotal?: number

  customType?: CustomType | null
  customUnit?: string | null
}

interface IProgressCellState {
  isTooltipVisible: boolean
}

export class ProgressCell extends React.PureComponent<IProgressCellProps, IProgressCellState> {
  state = { isTooltipVisible: false }
  tooltipTimer: any

  componentWillUnmount() {
    this.clearTimer()
  }

  hasDescription = () => {
    if (this.props.description) {
      return <p className="progress-cell__description">{this.props.description}</p>
    }

    return null
  }

  toggleTooltip = () => {
    this.state.isTooltipVisible ? this.hideTooltip() : this.showTooltip()
  }

  showTooltip = () => {
    this.clearTimer()

    this.setState({ isTooltipVisible: true }, () => {
      this.tooltipTimer = setTimeout(() => {
        this.hideTooltip()
      }, 3000)
    })
  }

  hideTooltip = () => {
    this.clearTimer()

    this.setState({ isTooltipVisible: false })
  }

  clearTimer = () => {
    if (this.tooltipTimer) {
      clearTimeout(this.tooltipTimer)
    }
  }

  BEM = () => {
    const classArr = ['progress-cell']
    const { progress, activityFormulaTarget } = this.props
    const target = activityFormulaTarget || 100

    if (progress && progress >= target) {
      classArr.push('progress-cell--active')
    }

    if (progress && progress >= target) {
      classArr.push('progress-cell--overflow')
    }

    return classArr.join(' ')
  }

  renderAllProgressCell() {
    const { caloriesTotal, intensiveMinutesTotal, stepsTotal, progress } = this.props
    const { isTooltipVisible } = this.state

    return (
      <div className="progress-cell__value">
        <Tooltip isTooltipVisible={isTooltipVisible}>
          <HeartIcon className="progress-cell__tooltip-icon" />
          <span>{formatThousands(intensiveMinutesTotal || 0)}</span>
          <span className="progress-cell__tooltip-content-separator"></span>
          <StepsIcon className="progress-cell__tooltip-icon progress-cell__tooltip-icon--steps" />
          <span>{formatThousands(stepsTotal || 0)}</span>
          <span className="progress-cell__tooltip-content-separator"></span>
          <CaloriesIcon className="progress-cell__tooltip-icon progress-cell__tooltip-icon--calories" />
          <span>{formatThousands(caloriesTotal || 0)}</span>
        </Tooltip>
        <div>
          <span>{progress}</span> <span style={{ fontSize: 12 }}>%</span>
        </div>
      </div>
    )
  }

  renderCustomProgressCell() {
    const { persistenceTotal, customType, customUnit } = this.props
    const unit = getCustomUnitText(customType, customType, customUnit)
    const isDistance = isDistanceType(customType)
    return (
      <div className="progress-cell__value">
        <div>
          <span>
            {formatThousands(persistenceTotal || 0, isDistance ? 2 : undefined)} {unit}
          </span>
        </div>
      </div>
    )
  }

  renderSpecificProgressCell() {
    const {
      progress,
      progressType,
      stepsTotal,
      caloriesTotal,
      intensiveMinutesTotal,
      customType,
      persistenceTotal,
      customUnit,
    } = this.props
    const { isTooltipVisible } = this.state
    const Icon =
      progressType && [ProgressType.CALORIES, ProgressType.COLLECTIVE_CALORIES].includes(progressType)
        ? CaloriesIcon
        : progressType && [ProgressType.STEPS, ProgressType.COLLECTIVE_STEPS].includes(progressType)
        ? StepsIcon
        : MinutesIcon
    const isCollectiveCustom = progressType === ProgressType.COLLECTIVE_CUSTOM
    const isDistance = isDistanceType(customType)
    const value =
      progressType && [ProgressType.CALORIES, ProgressType.COLLECTIVE_CALORIES].includes(progressType)
        ? caloriesTotal
        : progressType && [ProgressType.STEPS, ProgressType.COLLECTIVE_STEPS].includes(progressType)
        ? stepsTotal
        : isCollectiveCustom
        ? persistenceTotal
        : intensiveMinutesTotal
    const unit = getCustomUnitText(customType, customType, customUnit) as string

    return (
      <div className="progress-cell__value">
        {progress !== undefined && (
          <Tooltip isTooltipVisible={isTooltipVisible}>
            <span>{progress}</span> <span style={{ fontSize: 12 }}>%</span>
          </Tooltip>
        )}

        <div>
          <span>{formatThousands(value || 0, isDistance ? 2 : 0)}</span>
          {isCollectiveCustom ? (
            ' ' + unit
          ) : (
            <Icon className="progress-cell__tooltip-icon progress-cell__tooltip-icon--steps-blue" />
          )}
        </div>
      </div>
    )
  }

  renderPersistenceProgressCell(progress: number | undefined) {
    const { persistenceTotal } = this.props
    const days = persistenceTotal || 0
    return (
      <div className="progress-cell__value">
        <div className={progress === undefined ? 'text-right' : ''}>
          {persistenceTotal} <span style={{ fontWeight: 'normal' }}>{ngettext(msgid`day`, `days`, days)}</span>
        </div>
      </div>
    )
  }

  render() {
    const { progressType, progress, activityFormulaTarget } = this.props

    let calculatedProgress = progress !== undefined ? progress : false
    if (progress !== undefined && progressType !== ProgressType.STEPS && activityFormulaTarget) {
      calculatedProgress = Math.round(progress * (100 / activityFormulaTarget))
    }

    const isPersistenceChallenge = progressType && isPersistenceType(progressType)

    return (
      <div
        className={this.BEM()}
        onClick={progressType ? this.toggleTooltip : undefined}
        onMouseEnter={progressType ? this.showTooltip : undefined}
      >
        {!progressType || progressType === ProgressType.ALL
          ? this.renderAllProgressCell()
          : progressType === ProgressType.CUSTOM
          ? this.renderCustomProgressCell()
          : isPersistenceChallenge
          ? this.renderPersistenceProgressCell(progress)
          : this.renderSpecificProgressCell()}
        {progress !== undefined && (
          <>
            <div className="progress-cell__goal">
              <div
                className="progress-cell__progressbar"
                style={{
                  width: `${calculatedProgress}%`,
                }}
              />
            </div>
          </>
        )}
        {this.hasDescription()}
      </div>
    )
  }
}
